import * as React from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
} from '@mui/material'
import { aceColors, aceConvention } from '../../data/aceConvention'

const AceGongForm = ({ onUnlocked, onFormStart, onFormSubmit, resetNotice = false }) => {
  const [name, setName] = React.useState('')
  const [email, setEmail] = React.useState('')
  const [storeLocation, setStoreLocation] = React.useState('')
  const [errors, setErrors] = React.useState({})
  const [submitting, setSubmitting] = React.useState(false)
  const [submitError, setSubmitError] = React.useState('')
  const startedRef = React.useRef(false)

  const markStarted = () => {
    if (startedRef.current) return
    startedRef.current = true
    onFormStart?.()
  }

  const validate = () => {
    const e = {}
    if (!name.trim()) e.name = 'Name is required'
    if (!email.trim()) e.email = 'Email is required'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = 'Invalid email'
    if (!storeLocation.trim()) e.storeLocation = 'ACE Hardware store location is required'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSubmit = async (ev) => {
    ev.preventDefault()
    setSubmitError('')
    if (!validate()) return
    try {
      setSubmitting(true)
      const payload = {
        event: 'ace_gong_unlock',
        firstName: name.trim(),
        lastName: '—',
        name: name.trim(),
        email: email.trim(),
        phone: '',
        company: storeLocation.trim(),
        role: 'Other',
        jobLevel: 'Other',
        interest: 'Automation',
        message: `ACE Gong access request. Store: ${storeLocation.trim()}. Source: ${aceConvention.routes.gong}. Campaign: ${aceConvention.campaign}.`,
        source: aceConvention.gongLeadSource,
        lead_source: aceConvention.gongLeadSource,
        campaign: aceConvention.campaign,
        store_location: storeLocation.trim(),
        page: aceConvention.routes.gong,
        gong_access: true,
        timestamp: new Date().toISOString(),
      }
      const res = await fetch(aceConvention.gongWebhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(text || `Request failed (${res.status})`)
      }
      const visitor = {
        name: name.trim(),
        email: email.trim(),
        storeLocation: storeLocation.trim(),
      }
      onFormSubmit?.(visitor)
      onUnlocked?.(visitor)
    } catch (err) {
      setSubmitError(err.message || 'Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Box sx={{ width: '100%', maxWidth: 480, mx: 'auto' }}>
      <Typography
        component="h1"
        sx={{
          textAlign: 'center',
          fontWeight: 900,
          fontSize: { xs: '1.85rem', sm: '2.4rem' },
          color: '#fff',
          letterSpacing: '-0.02em',
          mb: 1,
          lineHeight: 1.1,
        }}
      >
        Ring the Workload Gong
      </Typography>
      <Typography
        sx={{
          textAlign: 'center',
          fontWeight: 700,
          color: aceColors.blueSoft,
          mb: 1.5,
          fontSize: '1.05rem',
        }}
      >
        Unlock the button. Hit it. Hear it.
      </Typography>
      <Typography
        sx={{
          textAlign: 'center',
          color: 'rgba(255,255,255,0.78)',
          lineHeight: 1.65,
          mb: 3,
          fontSize: '0.98rem',
        }}
      >
        Enter your ACE Hardware store location, name, and email to unlock the gong. Each visit gets a mystery
        number of hits—make them count.
      </Typography>

      {resetNotice ? (
        <Alert
          severity="warning"
          sx={{
            mb: 2,
            bgcolor: 'rgba(255,248,240,0.95)',
            color: aceColors.ink,
            '& .MuiAlert-icon': { color: aceColors.red },
          }}
        >
          You hit the max number of gong hits. Re-enter your details for another round.
        </Alert>
      ) : null}

      <Card
        variant="outlined"
        sx={{
          borderRadius: 3,
          borderColor: 'rgba(255,255,255,0.12)',
          boxShadow: '0 24px 64px -24px rgba(0,0,0,0.55)',
          bgcolor: aceColors.cream,
        }}
      >
        <CardContent component="form" onSubmit={handleSubmit} noValidate sx={{ p: { xs: 2.5, sm: 3 } }}>
          <TextField
            fullWidth
            required
            label="ACE Hardware store location"
            margin="normal"
            autoComplete="organization"
            value={storeLocation}
            onChange={(e) => {
              markStarted()
              setStoreLocation(e.target.value)
            }}
            error={!!errors.storeLocation}
            helperText={errors.storeLocation}
          />
          <TextField
            fullWidth
            required
            label="Name"
            margin="normal"
            autoComplete="name"
            value={name}
            onChange={(e) => {
              markStarted()
              setName(e.target.value)
            }}
            error={!!errors.name}
            helperText={errors.name}
          />
          <TextField
            fullWidth
            required
            type="email"
            label="Email"
            margin="normal"
            autoComplete="email"
            inputMode="email"
            value={email}
            onChange={(e) => {
              markStarted()
              setEmail(e.target.value)
            }}
            error={!!errors.email}
            helperText={errors.email}
          />
          {submitError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {submitError}
            </Alert>
          ) : null}
          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={submitting}
            sx={{
              mt: 2.5,
              py: 1.6,
              fontWeight: 800,
              bgcolor: aceColors.red,
              '&:hover': { bgcolor: aceColors.redDeep },
            }}
          >
            {submitting ? 'Unlocking…' : 'Unlock the gong'}
          </Button>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
            By submitting, you agree we may follow up about Workload. Unsubscribe anytime from marketing mail.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  )
}

export default AceGongForm
